import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { useEstadisticasModel } from '../../../modelos/Estadisticas/modeloEstadisticas'
import { toYYYYMMDD } from '../../../utils/utils';
import React, { useState, useEffect, useMemo } from 'react';

// format cosas
const formatCurrency = (value) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value);
const formatPercent = (value) => `${(value || 0).toFixed(2)}%`;
const formatNumber = (value) => new Intl.NumberFormat('es-CO').format(value);
const formatRatio = (value) => (value || 0).toFixed(2);


const ModuloMarketing = () => {

    const {
        estadisticasMarketing,
        loadingestadisticasMarketing,
        errorestadisticasMarketing,
        cargarEstadisticasMarketing
    } = useEstadisticasModel();
    const { configuracion } = useConfiguracion();

    // 1. ESTADO PARA ORDENAMIENTO Y FILTRO
    const [searchText, setSearchText] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'campania', direction: 'asc' }); 

    // fechas
    const [fechaInicio, setFechaInicio] = useState(() => {
        const hoy = new Date();
        const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        return toYYYYMMDD(primerDia);
    });
    const [fechaFinal, setFechaFinal] = useState(() => {
        const hoy = new Date(); 
        return toYYYYMMDD(hoy); 
    });

    useEffect(() => {
        cargarEstadisticasMarketing(configuracion.nombreEmpresa, fechaInicio, fechaFinal);
    }, [configuracion.nombreEmpresa, fechaInicio, fechaFinal]);

    // 🛑 Desestructuración Segura: Usamos el operador || {} para asegurar que totales y tablaCampanias
    // siempre existen (aunque sean objetos o arrays vacíos) si estadisticasMarketing es null/undefined.
    const { totales = {}, tablaCampanias = [] } = estadisticasMarketing || {};

    // 2. LÓGICA DE FILTRADO Y ORDENAMIENTO CON useMemo (POSICIÓN CORRECTA: Antes de returns)
    // Ahora depende de 'tablaCampanias', que será un array vacío si no hay datos aún.
    const sortedAndFilteredCampanias = useMemo(() => {
        let sortableItems = [...tablaCampanias];
        
        // 2.1. FILTRADO (BÚSQUEDA)
        if (searchText) {
            const lowerCaseSearch = searchText.toLowerCase();
            sortableItems = sortableItems.filter(item => 
                item.campania.toLowerCase().includes(lowerCaseSearch)
            );
        }

        // 2.2. ORDENAMIENTO
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                
                // Manejar valores nulos o indefinidos
                if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
                if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0; // Si son iguales
            });
        }
        return sortableItems;
    }, [tablaCampanias, searchText, sortConfig]); // Depende del array vacío o de los datos cargados


    const StatCard = ({ title, value, icon, format = formatNumber }) => (
        <div className="bg-white p-6 rounded-xl shadow-md flex items-start space-x-4">
            <div className="bg-blue-100 p-3 rounded-lg">{icon}</div>
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-800">{format(value)}</p>
            </div>
        </div>
    );
    
    // Función para manejar el clic en el encabezado de la tabla
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Componente auxiliar para el icono de ordenamiento
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return null;
        return (
            <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-4 w-4 inline ml-1 transition-transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} 
                viewBox="0 0 20 20" 
                fill="currentColor"
            >
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
        );
    };

    // Función auxiliar para crear encabezados de tabla clickeables
    const ThButton = ({ children, onClick, columnKey }) => (
        <th scope="col" className="px-6 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors" onClick={() => onClick(columnKey)}>
            <div className="flex items-center">
                {children}
                <SortIcon columnKey={columnKey} />
            </div>
        </th>
    );
    
    // 3. RETORNOS CONDICIONALES (AHORA SEGURAMENTE DESPUÉS DE LOS HOOKS)
    if (loadingestadisticasMarketing) {
        return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div></div>;
    }

    if (errorestadisticasMarketing) {
        return <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md m-4" role="alert">{errorestadisticasMarketing}</div>;
    }
    
    if (!estadisticasMarketing) {
        return <div className="text-center p-8">No hay datos de marketing para mostrar.</div>;
    }


    return (
        <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
            <div className="mx-auto space-y-8 px-4">
                {/* Cabecera y Filtros de Fecha */}
                <header className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                    <h1 className="text-3xl font-bold text-gray-900">Estadísticas de Marketing</h1>
                    <div className="flex items-center space-x-4">
                        <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="bg-white border border-gray-300 rounded-md p-2 text-sm"/>
                        <span className="text-gray-500">-</span>
                        <input type="date" value={fechaFinal} onChange={e => setFechaFinal(e.target.value)} className="bg-white border border-gray-300 rounded-md p-2 text-sm"/>
                    </div>
                </header>

                {/* Grid de KPIs Totales */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard title="Leads" value={totales.leads} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                    <StatCard title="Prospectos" value={totales.prospectos} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                    <StatCard title="Clientes" value={totales.clientes} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} />
                    <StatCard title="Ganancia Inicial" value={totales.gananciaInicial} format={formatCurrency} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01" /></svg>} />
                    <StatCard title="Ganancia Total" value={totales.gananciaTotal} format={formatCurrency} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01" /></svg>} />
                    <StatCard title="Costo Total" value={totales.costoTotalCampanas} format={formatCurrency} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>} />
                    <StatCard title="ROI INICIAL" value={totales.roiInicial} format={formatPercent} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} />
                    <StatCard title="ROI Total" value={totales.roiTotal} format={formatPercent} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} />
                    <StatCard title="ROAS Inicial" value={totales.RoasInicial} format={formatPercent} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} />
                    <StatCard title="ROAS Total" value={totales.RoasTotal} format={formatPercent} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} />
                </div>
                
                {/* Tabla de Campañas */}
                <div className="bg-white p-6 rounded-xl shadow-md">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Rendimiento por Campaña</h2>
                    
                    {/* Input de Búsqueda/Filtro */}
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Buscar por nombre de campaña..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            className="w-full sm:w-1/3 bg-gray-50 border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    {/* Encabezados clickeables */}
                                    <ThButton onClick={requestSort} columnKey="campania">Campaña</ThButton>
                                    <ThButton onClick={requestSort} columnKey="leads">Leads</ThButton>
                                    <ThButton onClick={requestSort} columnKey="prospectos">Prospectos</ThButton>
                                    <ThButton onClick={requestSort} columnKey="clientes">Clientes</ThButton>
                                    <ThButton onClick={requestSort} columnKey="gananciaInicial">G. inicial</ThButton>
                                    <ThButton onClick={requestSort} columnKey="gananciaTotal">G. Total</ThButton>
                                    <ThButton onClick={requestSort} columnKey="costoCampana">Costo</ThButton>
                                    <ThButton onClick={requestSort} columnKey="roiInicial">ROI Inicial</ThButton>
                                    <ThButton onClick={requestSort} columnKey="roiTotal">ROI Total</ThButton>
                                    <ThButton onClick={requestSort} columnKey="RoasInicial">ROAS inicial</ThButton>
                                    <ThButton onClick={requestSort} columnKey="RoasTotal">ROAS Total</ThButton>

                                    {/* Las columnas de fecha no necesitan ser ordenables por su clave, pero las mantendremos en la estructura para la estética */}
                                    <th scope="col" className="px-6 py-3">Inicio</th>
                                    <th scope="col" className="px-6 py-3">Final</th>
                                    <th scope="col" className="px-6 py-3">Primer servicio</th>
                                    <th scope="col" className="px-6 py-3">Ultimo servicio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAndFilteredCampanias.map((item, index) => (
                                    <tr key={index} className="bg-white border-b hover:bg-gray-50">
                                        <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{item.campania}</th>
                                        <td className="px-6 py-4">{formatNumber(item.leads)}</td>
                                        <td className="px-6 py-4">{formatNumber(item.prospectos)}</td>
                                        <td className="px-6 py-4">{formatNumber(item.clientes)}</td>
                                        <td className="px-6 py-4">{formatCurrency(item.gananciaInicial)}</td>
                                        <td className="px-6 py-4">{formatCurrency(item.gananciaTotal)}</td>
                                        <td className="px-6 py-4 text-red-600">{formatCurrency(item.costoCampana)}</td>
                                        <td className={`px-6 py-4 font-bold ${item.roiInicial >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatPercent(item.roiInicial)}
                                        </td>
                                        <td className={`px-6 py-4 font-bold ${item.roiTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatPercent(item.roiTotal)}
                                        </td>
                                        <td className={`px-6 py-4 font-bold ${item.RoasInicial >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatPercent(item.RoasInicial)}
                                        </td>
                                        <td className={`px-6 py-4 font-bold ${item.RoasTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatPercent(item.RoasTotal)}
                                        </td>

                                        {/* Fechas*/}
                                        <td className="px-6 py-4">{item.fechasActividad.inicioInversion}</td>
                                        <td className="px-6 py-4">{item.fechasActividad.finInversion}</td>
                                        <td className="px-6 py-4">{item.fechasActividad.primerServicio}</td>
                                        <td className="px-6 py-4">{item.fechasActividad.ultimoServicio}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sortedAndFilteredCampanias.length === 0 && (
                            <div className="text-center p-8 text-gray-500">
                                No se encontraron campañas que coincidan con la búsqueda.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )

}

export default ModuloMarketing